import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import type { Selection, ToolMode } from '../types';

export type ToolId = 'render-bridge' | 'lens' | 'magic-touch' | 'refiner' | 'gallery' | 'axes' | 'interior';

export interface BuildingData {
    id: string;
    pinLabel: string;
    name: string;
    description: string;
    x?: number;
    y?: number;
}

interface ToolState {
    activeTool: ToolId;
    setActiveTool: (tool: ToolId) => void;

    // Shared Tool State
    compareMode: boolean;
    setCompareMode: (enabled: boolean) => void;
    uploadedImage: string | null;
    setUploadedImage: (image: string | null) => void;

    // Shared Interaction State
    prompt: string;
    setPrompt: (prompt: string) => void;
    generationTrigger: number;
    triggerGeneration: () => void;

    // Canvas State
    toolMode: ToolMode;
    // Land Typology State
    landTypology: 'garden' | 'park';
    setLandTypology: (type: 'garden' | 'park') => void;
    landSettings: {
        // House Garden
        lawnArea: number;
        patioSize: number;
        shrubDensity: number;
        // Park
        greeneryDensity: number;
        waterFeatures: number;
        pathCurve: number;
    };
    setLandSettings: (settings: Partial<{
        lawnArea: number;
        patioSize: number;
        shrubDensity: number;
        greeneryDensity: number;
        waterFeatures: number;
        pathCurve: number;
    }>) => void;
    variationsCount: number;
    setVariationsCount: (count: number) => void;

    // Actions
    setToolMode: (mode: ToolMode) => void;
    brushSize: number;
    setBrushSize: (size: number) => void;
    brushColor: string;
    setBrushColor: (color: string) => void;
    selection: Selection | null;
    setSelection: (selection: Selection | null) => void;

    // History State
    history: string[];
    historyIndex: number;
    pushToHistory: (image: string) => void;
    undo: () => void;
    redo: () => void;
    setHistory: (history: string[]) => void;
    setHistoryIndex: (index: number) => void;

    // Tool Specific Params (can be expanded later)
    renderBridgeParams: {
        timeOfDay: number; // 0-100
        weather: string[];
        drama: number;
    };
    lensParams: {
        location: string;
        showMap: boolean;
        sunPosition: string;
        entourage: {
            crowdDensity: number;
            demographic: string;
            activity: string;
            ghostMode: boolean;
        };
        camera: {
            viewpoints: string[];
            geometryLock: boolean;
        };
        buildings: BuildingData[];
    };
    planToViewParams: {
        sourceType: string;
        structuralAdherence: number;
        outputView: string;
        stylePreset: string;
        sectionCut: boolean;
    };
    refinerParams: {
        imageMode: string;
        action: string;
        modifySelectionOnly: boolean;
    };
    materialOverrideParams: {
        textureUrl: string | null;
        textureScale: number;
        applyTarget: string | null; // e.g., "walls", "ground" (future proofing)
    };
    interiorParams: {
        theme: string;
        paletteId: string;
        timeOfDay: string;
        roomType: string;
    };

    // Setters
    setRenderBridgeParams: (params: Partial<ToolState['renderBridgeParams']>) => void;
    setLensParams: (params: Partial<ToolState['lensParams']>) => void;
    setPlanToViewParams: (params: Partial<ToolState['planToViewParams']>) => void;
    setRefinerParams: (params: Partial<ToolState['refinerParams']>) => void;
    setMaterialOverrideParams: (params: Partial<ToolState['materialOverrideParams']>) => void;
    setInteriorParams: (params: Partial<ToolState['interiorParams']>) => void;

    // Generic Setter for Universal Toolbar
    setGenericToolParam: (path: string, value: any) => void;

    // UI State
    isPanelOpen: boolean;
    togglePanel: () => void;
    setPanelOpen: (isOpen: boolean) => void;
}

export const useToolStore = create<ToolState>()(
    immer((set) => ({
        activeTool: 'render-bridge',
        setActiveTool: (tool) =>
            set((state) => {
                state.activeTool = tool;
            }),
        compareMode: false,
        setCompareMode: (enabled) =>
            set((state) => {
                state.compareMode = enabled;
            }),
        uploadedImage: null,
        setUploadedImage: (image) =>
            set((state) => {
                state.uploadedImage = image;
                // When a new image is uploaded, reset history
                if (image) {
                    state.history = [image];
                    state.historyIndex = 0;
                    state.selection = null;
                } else {
                    state.history = [];
                    state.historyIndex = -1;
                    state.selection = null;
                }
            }),

        // Prompt State
        prompt: '',
        setPrompt: (prompt) =>
            set((state) => {
                state.prompt = prompt;
            }),
        generationTrigger: 0,
        triggerGeneration: () =>
            set((state) => {
                state.generationTrigger += 1;
            }),

        // Canvas State
        toolMode: 'freehand',
        setToolMode: (mode) =>
            set((state) => {
                state.toolMode = mode;
                state.selection = null; // Clear selection on mode change
            }),
        brushSize: 50,
        setBrushSize: (size) =>
            set((state) => {
                state.brushSize = size;
            }),
        brushColor: '#ffffff',
        setBrushColor: (color) =>
            set((state) => {
                state.brushColor = color;
            }),
        selection: null,
        setSelection: (selection) =>
            set((state) => {
                state.selection = selection;
            }),

        // History State
        history: [],
        historyIndex: -1,
        pushToHistory: (image) =>
            set((state) => {
                const newHistory = [...state.history.slice(0, state.historyIndex + 1), image];
                state.history = newHistory;
                state.historyIndex = newHistory.length - 1;
                // state.selection = null; // Keep selection for rapid iteration (User Request)
            }),
        undo: () =>
            set((state) => {
                if (state.historyIndex > 0) {
                    state.historyIndex -= 1;
                    state.selection = null;
                    if (state.historyIndex === 0) state.compareMode = false;
                }
            }),
        redo: () =>
            set((state) => {
                if (state.historyIndex < state.history.length - 1) {
                    state.historyIndex += 1;
                    state.selection = null;
                }
            }),
        setHistory: (history) =>
            set((state) => {
                state.history = history;
            }),
        setHistoryIndex: (index) =>
            set((state) => {
                state.historyIndex = index;
            }),

        // Tool Setters
        setRenderBridgeParams: (params) =>
            set((state) => {
                state.renderBridgeParams = { ...state.renderBridgeParams, ...params };
            }),
        setLensParams: (params) =>
            set((state) => {
                state.lensParams = { ...state.lensParams, ...params };
            }),
        setPlanToViewParams: (params) =>
            set((state) => {
                state.planToViewParams = { ...state.planToViewParams, ...params };
            }),
        setRefinerParams: (params) =>
            set((state) => {
                state.refinerParams = { ...state.refinerParams, ...params };
            }),
        setMaterialOverrideParams: (params) =>
            set((state) => {
                state.materialOverrideParams = { ...state.materialOverrideParams, ...params };
            }),
        setInteriorParams: (params) =>
            set((state) => {
                state.interiorParams = { ...state.interiorParams, ...params };
            }),

        setGenericToolParam: (path, value) =>
            set((state) => {
                // Simple dot notation setter
                const keys = path.split('.');
                let current: any = state;
                for (let i = 0; i < keys.length - 1; i++) {
                    current = current[keys[i]];
                }
                current[keys[keys.length - 1]] = value;
            }),

        renderBridgeParams: {
            timeOfDay: 50,
            weather: ['Clear'],
            drama: 0,
        },
        lensParams: {
            location: '',
            showMap: false,
            sunPosition: 'Noon',
            entourage: {
                crowdDensity: 30,
                demographic: 'Leisure',
                activity: 'Walking',
                ghostMode: false,
            },
            camera: {
                viewpoints: ['Eye Level'],
                geometryLock: true,
            },
            buildings: [
                { id: 'bld-A', pinLabel: 'A', name: 'PFBF Stadium', description: 'Primary focal sports stadium structure with expansive grandstands and open pitch.' },
                { id: 'bld-B', pinLabel: 'B', name: 'Equestrian stand', description: 'Outdoor tiered spectator grandstand structure designed for viewing equestrian events.' },
                { id: 'bld-C', pinLabel: 'C', name: 'Gymnasium', description: 'Large indoor sports hall dedicated to martial arts, featuring wide spans and robust massing.' }
            ],
        },
        planToViewParams: {
            sourceType: 'Hand Sketch',
            structuralAdherence: 50,
            outputView: 'Axonometric',
            stylePreset: 'Photoreal',
            sectionCut: false,
        },
        refinerParams: {
            imageMode: 'Perspective View',
            action: 'Modify/Replace Object',
            modifySelectionOnly: true,
        },
        materialOverrideParams: {
            textureUrl: null,
            textureScale: 1,
            applyTarget: null
        },
        interiorParams: {
            theme: 'modern',
            paletteId: 'neutral',
            timeOfDay: 'Day',
            roomType: 'living_room'
        },

        // Land Typology Initial State
        landTypology: 'garden',
        setLandTypology: (type) =>
            set((state) => {
                state.landTypology = type;
            }),
        landSettings: {
            lawnArea: 50,
            patioSize: 30,
            shrubDensity: 50,
            greeneryDensity: 70,
            waterFeatures: 30,
            pathCurve: 50
        },
        setLandSettings: (settings) =>
            set((state) => {
                state.landSettings = { ...state.landSettings, ...settings };
            }),
        variationsCount: 1,
        setVariationsCount: (count) =>
            set((state) => {
                state.variationsCount = count;
            }),

        // UI State Implementation
        isPanelOpen: true, // Default to open, will be adjusted by component
        togglePanel: () =>
            set((state) => {
                state.isPanelOpen = !state.isPanelOpen;
            }),
        setPanelOpen: (isOpen) =>
            set((state) => {
                state.isPanelOpen = isOpen;
            }),
    }))
);
